import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { amazonApi } from "../../../services/api/multi/amazon";

const AmazonCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme, "amazon");

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;

    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        return detail
          .map((err) => `${err.loc?.join(".")}: ${err.msg}`)
          .join(", ");
      }
      return JSON.stringify(detail);
    }

    if (error.response?.data?.message) {
      return typeof error.response.data.message === "string"
        ? error.response.data.message
        : JSON.stringify(error.response.data.message);
    }

    if (error.message) {
      return typeof error.message === "string"
        ? error.message
        : JSON.stringify(error.message);
    }

    return JSON.stringify(error);
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Amazon State
  const [amazonCredentials, setAmazonCredentials] = useState(() => {
    try {
      const stored = localStorage.getItem("amazonCredentials");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [form, setForm] = useState({
    id: "",
    display_name: "",
    client_id: "",
    client_secret: "",
    refresh_token: "",
  });
  const [formTestStatus, setFormTestStatus] = useState("");

  useEffect(() => {
    loadCredentials();
  }, []);

  useEffect(() => {
    if (snackbarOpen) {
      const timer = setTimeout(() => setSnackbarOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbarOpen]);

  const loadCredentials = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await amazonApi.getAll();
      const credentials = response.data || [];

      setAmazonCredentials(credentials);
      localStorage.setItem("amazonCredentials", JSON.stringify(credentials));
    } catch (error) {
      console.error("Failed to load Amazon credentials:", error);
      setError(getErrorMessage(error));
      setAmazonCredentials([]);
      localStorage.setItem("amazonCredentials", JSON.stringify([]));
    } finally {
      setLoading(false);
    }
  };

  const handleEditCredential = (credential) => {
    setEditingCredential(credential);
    setForm({
      id: credential.id || "",
      display_name: credential.display_name || "",
      client_id: credential.client_id || "",
      client_secret: credential.client_secret || "",
      refresh_token: credential.refresh_token || "",
    });
    setFormTestStatus("");
    setDialogOpen(true);
  };

  const createCredential = async () => {
    if (
      !form.display_name ||
      !form.client_id ||
      !form.client_secret ||
      !form.refresh_token
    ) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await amazonApi.create(form);
      const newCredential = response.data || {};

      setAmazonCredentials((prev) => [...prev, newCredential]);
      setSuccess("Amazon credential created successfully!");
      setSnackbarOpen(true);

      setDialogOpen(false);
      setForm({
        id: "",
        display_name: "",
        client_id: "",
        client_secret: "",
        refresh_token: "",
      });
    } catch (error) {
      setError(getErrorMessage(error));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const updateCredential = async () => {
    if (
      !form.display_name ||
      !form.client_id ||
      !form.client_secret ||
      !form.refresh_token
    ) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await amazonApi.update(editingCredential.id, form);
      const updatedCred = response.data;

      setAmazonCredentials((prev) =>
        prev.map((c) => (c.id === updatedCred.id ? updatedCred : c)),
      );

      setSuccess("Amazon credential updated successfully!");
      setSnackbarOpen(true);

      setDialogOpen(false);
      setEditingCredential(null);
      setForm({
        id: "",
        display_name: "",
        client_id: "",
        client_secret: "",
        refresh_token: "",
      });
    } catch (error) {
      setError(getErrorMessage(error));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const deleteCredential = async (credentialId) => {
    try {
      setLoading(true);
      await amazonApi.delete(credentialId);

      setAmazonCredentials((prev) => prev.filter((c) => c.id !== credentialId));
      setSuccess("Amazon credential deleted successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      setError(getErrorMessage(error));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleCredentialActive = async (credentialId) => {
    try {
      setLoading(true);
      await amazonApi.toggleActive(credentialId);

      // We reload all to ensure other toggles are correctly un-marked if
      // the backend keeps only 1 active at a time.
      await loadCredentials();

      setSuccess("Credential active status updated!");
      setSnackbarOpen(true);
    } catch (error) {
      setError(getErrorMessage(error));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const testCredentialInForm = async () => {
    if (!form.client_id || !form.client_secret || !form.refresh_token) {
      setFormTestStatus("❌ Missing required fields");
      return;
    }

    try {
      setFormTestStatus("🔄 Testing credentials...");
      await amazonApi.test({
        client_id: form.client_id,
        client_secret: form.client_secret,
        refresh_token: form.refresh_token,
      });
      setFormTestStatus("✅ Credentials verified successfully!");
    } catch (error) {
      console.error(error);
      setFormTestStatus("❌ Credential verification failed!");
    }
  };

  const testSavedCredential = async (credentialId) => {
    try {
      setLoading(true);
      await amazonApi.testSaved(credentialId);
      setSuccess("Amazon SP-API Connection Successful!");
      setSnackbarOpen(true);
      await loadCredentials();
    } catch (error) {
      setError(getErrorMessage(error));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card
          sx={{
            border: "3px solid",
            borderColor: "warning.main",
            borderRadius: 3,
            boxShadow: 3,
            ...cp.outerCard,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              sx={{
                ...cp.header,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "auto",
                  paddingRight: 2,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  📦 Amazon SP-API Credentials
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage your Amazon Selling Partner API credentials
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, marginLeft: "auto" }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadCredentials}
                  disabled={loading}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reload
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setForm({
                      id: "",
                      display_name: "",
                      client_id: "",
                      client_secret: "",
                      refresh_token: "",
                    });
                    setEditingCredential(null);
                    setFormTestStatus("");
                    setDialogOpen(true);
                  }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Credentials
                </Button>
              </Box>
            </Box>

            <Alert
              severity="info"
              sx={cp.infoAlert}
              icon={<CheckCircleIcon sx={{ color: "info.main" }} />}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                📋 How to Get Your Amazon SP-API Credentials:
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 3 }}>
                <li>Register a Developer Profile in Amazon Seller Central</li>
                <li>
                  Create an Application to get the Client ID and Client Secret
                </li>
                <li>Authorize the Application to obtain the Refresh Token</li>
              </Box>
            </Alert>

            {amazonCredentials.length === 0 ? (
              <Box sx={cp.emptyState}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "text.secondary" }}
                >
                  No Amazon Credentials Found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Add your first Amazon SP-API credential to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setDialogOpen(true)}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    ...cp.ctaButton,
                    boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
                  }}
                >
                  Add Your First Credential
                </Button>
              </Box>
            ) : (
              <List
                sx={{
                  width: "100%",
                  border: "2px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                }}
              >
                {amazonCredentials.map((credential, index) => (
                  <ListItem
                    key={credential.id}
                    divider={index < amazonCredentials.length - 1}
                    sx={{
                      px: 4,
                      py: 3,
                      borderBottom:
                        index < amazonCredentials.length - 1
                          ? "2px solid"
                          : "none",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.2s ease",
                      borderRadius:
                        index === 0
                          ? "8px 8px 0 0"
                          : index === amazonCredentials.length - 1
                            ? "0 0 8px 8px"
                            : 0,
                    }}
                  >
                    <Grid container alignItems="center" spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Typography variant="h6">
                          {credential.display_name || "Amazon Account"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-all", pr: 2 }}
                        >
                          Client ID: {credential.client_id}
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.5,
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <Chip
                            label={
                              credential.authorized
                                ? "Authorized"
                                : "Unauthorized"
                            }
                            size="small"
                            color={credential.authorized ? "success" : "error"}
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "center", md: "flex-end" },
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mr: 2,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={credential.active_multi || false}
                                onChange={() =>
                                  toggleCredentialActive(credential.id)
                                }
                                size="small"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "success.main",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                      backgroundColor: "success.main",
                                    },
                                }}
                              />
                            }
                            label=""
                            sx={{ m: 0 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: credential.active_multi
                                ? "success.main"
                                : "text.secondary",
                              fontWeight: "bold",
                              mt: 0.5,
                            }}
                          >
                            {credential.active_multi ? "Active" : "Inactive"}
                          </Typography>
                        </Box>

                        <IconButton
                          onClick={() => testSavedCredential(credential.id)}
                          disabled={loading}
                          color="info"
                          title="Test Credentials"
                          sx={{
                            backgroundColor: "info.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "info.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckCircleIcon />
                          )}
                        </IconButton>
                        <IconButton
                          title="Edit Credential"
                          onClick={() => handleEditCredential(credential)}
                          color="warning"
                          sx={{
                            backgroundColor: "warning.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "warning.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deleteCredential(credential.id)}
                          title="Delete Credential"
                          sx={{
                            backgroundColor: "error.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "error.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingCredential(null);
          setFormTestStatus("");
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(90deg, #ff9800 0%, #f57c00 100%)",
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderRadius: "12px 12px 0 0",
          }}
        >
          {editingCredential
            ? "✏️ Edit Amazon Credentials"
            : "➕ Add Amazon Credentials"}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                placeholder="My Store Account"
                value={form.display_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, display_name: e.target.value }))
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client ID"
                value={form.client_id}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, client_id: e.target.value }))
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client Secret"
                type="password"
                value={form.client_secret}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    client_secret: e.target.value,
                  }))
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Refresh Token"
                type="password"
                value={form.refresh_token}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    refresh_token: e.target.value,
                  }))
                }
                required
              />
            </Grid>
            {formTestStatus && (
              <Grid item xs={12}>
                <Alert
                  severity={
                    formTestStatus.includes("✅")
                      ? "success"
                      : formTestStatus.includes("🔄")
                        ? "info"
                        : "error"
                  }
                  sx={{ mt: 1 }}
                >
                  {formTestStatus}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            justifyContent: "space-between",
            backgroundColor: "grey.50",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Button
            onClick={() => {
              setDialogOpen(false);
              setEditingCredential(null);
              setFormTestStatus("");
              setForm({
                id: "",
                display_name: "",
                client_id: "",
                client_secret: "",
                refresh_token: "",
              });
            }}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={testCredentialInForm}
            variant="outlined"
            disabled={
              loading ||
              !form.client_id ||
              !form.client_secret ||
              !form.refresh_token
            }
            sx={{ borderRadius: 2, px: 3 }}
          >
            {loading ? <CircularProgress size={20} /> : "Test Credentials"}
          </Button>
          <Button
            onClick={editingCredential ? updateCredential : createCredential}
            variant="contained"
            disabled={
              loading ||
              !form.display_name ||
              !form.client_id ||
              !form.client_secret ||
              !form.refresh_token
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              ...cp.ctaButton,
              boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
              "&:hover": {
                transform: "translateY(-2px)",
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processing...
              </Box>
            ) : editingCredential ? (
              "Update Credential"
            ) : (
              "Create Credential"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={error ? "error" : "success"} sx={{ width: "100%" }}>
          {error || success}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default AmazonCredentials;
